import { ActionTree, Module, MutationTree } from 'vuex'
import { RootState } from '@/interfaces/store/root'
import { Actions, Mutations, MyCouponInfoPayload, MyCouponInfoState } from '@/interfaces/store/myCouponInfo'
import { CouponsList, OffersList, PartnerOffersList } from '@/interfaces/my-coupon'
import * as meService from '@/services/line-web-loyalty-bff/v1/me'
import * as offerService from '@/services/coupon-center-bff/v1/offers'
import * as partnerOfferService from '@/services/coupon-center-bff/v1/partner-offers'
import * as customersService from '@/services/coupon-center-bff/v1/customers'
import liff from '@line/liff'
import dayjs from 'dayjs'
import { MyCouponType } from '@/model/my-coupon.model'

// Display priority of the coupon type in the coupon list:
// Cash Discount -> Category Discount -> Product Discount. Any other type keeps its
// relative order at the end of the list.
const COUPON_CATEGORY_ORDER = ['voucher', 'category', 'product']

const getCategoryRank = (coupon: CouponsList): number => {
  const index = COUPON_CATEGORY_ORDER.indexOf(coupon.category)
  return index === -1 ? COUPON_CATEGORY_ORDER.length : index
}

// A coupon is coming soon while the current time is still before its startDate.
const isCouponComingSoon = (coupon: CouponsList): boolean =>
  Boolean(coupon.startDate && dayjs().isBefore(dayjs(coupon.startDate)))

const compareCouponPriority = (a: CouponsList, b: CouponsList): number =>
  Number(isCouponComingSoon(a)) - Number(isCouponComingSoon(b)) ||
  getCategoryRank(a) - getCategoryRank(b)

// Voucher and premium offers are requested sorted by internalName and re-sorted here, the same way
// as the "recommend" sort in coupon-center: coming soon offers, then sold out offers, go to the end.
const OFFERS_QUERY = 'fields=availableInventoryRemaining&sortBy=internalName&limitToOnlyActive=false&rewardType=MASS'

const VOUCHER_OFFER_TYPES = ['Voucher', 'GoldVoucher']
const PREMIUM_OFFER_TYPES = ['Premium']

const filterOffersByType = (offers: OffersList[], types: string[]): OffersList[] =>
  offers.filter((offer) => types.includes(offer.offerDetails.offerType ?? ''))

const getOfferPriority = (offer: OffersList): number => {
  const redemptionTime = offer.redemptionStartDate ? new Date(offer.redemptionStartDate).getTime() : null
  if (redemptionTime !== null && !Number.isNaN(redemptionTime) && Date.now() < redemptionTime) return 1
  if (typeof offer.availableInventoryRemaining === 'number' && offer.availableInventoryRemaining === 0) return 2
  return 0
}

const sortOffers = (offers: OffersList[]): OffersList[] =>
  [...offers].sort(
    (a, b) =>
      getOfferPriority(a) - getOfferPriority(b) ||
      (a.offerDetails.internalName ?? '').localeCompare(b.offerDetails.internalName ?? '')
  )

export const state: MyCouponInfoState = {
  token: '',
  coupons: {
    isLoading: true,
    items: []
  },
  premiumOffers: {
    isLoading: true,
    items: []
  },
  partnerOffers: {
    isLoading: true,
    items: []
  },
  voucherOffers: {
    isLoading: true,
    items: []
  }
}

export const actions: ActionTree<MyCouponInfoState, RootState> & Actions = {
  // Reload every coupon section on the home page (e.g. after the customer tier changes)
  async reloadAllCoupons({ commit, dispatch, rootState }, payload: { token: string }) {
    const { token } = payload
    if (!token) return
    const tierType = rootState.tier?.tierName?.toLocaleLowerCase()
    const couponType = `${tierType === 'gold' ? 'Gold' : ''}${MyCouponType.SPECIAL_FOR_YOU}`
    commit('updateCouponsIsLoading', true)
    commit('updatePremiumOffersIsLoading', true)
    commit('updateVoucherOffersIsLoading', true)
    commit('updatePartnerOffersIsLoading', true)
    await dispatch('fetchCouponsList', { token, couponType })
    await dispatch('fetchPartnerOffersList', { token })
    await dispatch('fetchVoucherOffersList', { token })
    await dispatch('fetchPremiumOffersList', { token })
  },
  async fetchMyCouponToken({ commit }, payload: MyCouponInfoPayload) {
    commit('updateToken', '')
    try {
      const { lineIdToken } = payload
      if (lineIdToken) {
        const result = await meService.getToken(lineIdToken)
        const statusCode = result.httpStatusCode
        if (statusCode === 200) {
          const { token: myCouponToken } = result.json.data

          commit('updateToken', myCouponToken)
        } else if (statusCode === 401) {
          liff.logout()
          liff.login()
        } else {
          throw new Error('พบข้อผิดพลาดบางประการ กรุณาลองใหม่อีกครั้ง')
        }
      }
    } catch (error) {
      console.log(error)
    }
  },
  async fetchCouponsList({ commit }, payload: MyCouponInfoPayload) {
    commit('updateCouponsIsLoading', true)
    commit('updateCouponsList', [])

    try {
      const { token, couponType } = payload
      if (token) {
        const result = await customersService.getCouponsByType(token, couponType)
        const statusCode = result.httpStatusCode
        if (statusCode === 200) { 
          const data: CouponsList[] = result.json.data
          if (data.length > 0) {
            // Coming-soon coupons always sink to the bottom; the ones above are then
            // ordered by coupon type. sort() is stable, so coupons that tie on both
            // criteria keep the order the API returned them in.
            const response: CouponsList[] = [...data].sort(compareCouponPriority)
            commit('updateCouponsList', response)
          }
          commit('updateCouponsIsLoading', false)
        } else if (statusCode === 401) {
          liff.logout()
          liff.login()
        } else {
          throw new Error('พบข้อผิดพลาดบางประการ กรุณาลองใหม่อีกครั้ง')
        }
      }
    } catch (error) {
      console.log(error)
    }
    commit('updateCouponsIsLoading', false)
  },
  async fetchPremiumOffersList({ commit }, payload: MyCouponInfoPayload) {
    commit('updatePremiumOffersIsLoading', true)
    commit('updatePremiumOffersList', [])

    try {
      const { token } = payload
      if (token) {
        const result = await offerService.getOffers(token, undefined, OFFERS_QUERY)
        const statusCode = result.httpStatusCode
        if (statusCode === 200) { 
          const response: OffersList[] = result.json.data.length > 0 ? result.json.data : []
          commit('updatePremiumOffersList', sortOffers(filterOffersByType(response, PREMIUM_OFFER_TYPES)))
          commit('updatePremiumOffersIsLoading', false)
        } else if (statusCode === 401) {
          liff.logout()
          liff.login()
        } else {
          throw new Error('พบข้อผิดพลาดบางประการ กรุณาลองใหม่อีกครั้ง')
        }
      }
    } catch (error) {
      console.log(error)
    }
    commit('updatePremiumOffersIsLoading', false)
  },
  async fetchVoucherOffersList({ commit }, payload: MyCouponInfoPayload) {
    commit('updateVoucherOffersIsLoading', true)
    commit('updateVoucherOffersList', [])

    try {
      const { token } = payload
      if (token) {
        const result = await offerService.getOffers(token, undefined, OFFERS_QUERY)
        const statusCode = result.httpStatusCode
        if (statusCode === 200) { 
          const response: OffersList[] = result.json.data.length > 0 ? result.json.data : []
          commit('updateVoucherOffersList', sortOffers(filterOffersByType(response, VOUCHER_OFFER_TYPES)))
          commit('updateVoucherOffersIsLoading', false)
        } else if (statusCode === 401) {
          liff.logout()
          liff.login()
        } else {
          throw new Error('พบข้อผิดพลาดบางประการ กรุณาลองใหม่อีกครั้ง')
        }
      }
    } catch (error) {
      console.log(error)
    }
    commit('updateVoucherOffersIsLoading', false)
  },
  async fetchPartnerOffersList({ commit }, payload: MyCouponInfoPayload) {
    commit('updatePartnerOffersIsLoading', true)
    commit('updatePartnerOffersList', [])

    try {
      const { token } = payload
      if (token) {
        const result = await partnerOfferService.getPartnerOffers(token)
        const statusCode = result.httpStatusCode
        if (statusCode === 200) { 
          const response: PartnerOffersList[] = result.json.data.length > 0 ? result.json.data : []
          commit('updatePartnerOffersList', response)
          commit('updatePartnerOffersIsLoading', false)
        } else if (statusCode === 401) {
          liff.logout()
          liff.login()
        } else {
          throw new Error('พบข้อผิดพลาดบางประการ กรุณาลองใหม่อีกครั้ง')
        }
      }
    } catch (error) {
      console.log(error)
    }
    commit('updatePartnerOffersIsLoading', false)
  }
}

export const mutations: MutationTree<MyCouponInfoState> & Mutations = {
  updateToken(state: MyCouponInfoState, token: string) {
    state.token = token
  },
  updateCouponsIsLoading(state: MyCouponInfoState, loading: boolean) {
    state.coupons.isLoading = loading
  },
  updateCouponsList(state: MyCouponInfoState, couponList: CouponsList[]) {
    state.coupons.items = couponList
  },
  updatePremiumOffersIsLoading(state: MyCouponInfoState, loading: boolean) {
    state.premiumOffers.isLoading = loading
  },
  updatePremiumOffersList(state: MyCouponInfoState, premiumOfferList: OffersList[]) {
    state.premiumOffers.items = premiumOfferList
  },
  updateVoucherOffersIsLoading(state: MyCouponInfoState, loading: boolean) {
    state.voucherOffers.isLoading = loading
  },
  updateVoucherOffersList(state: MyCouponInfoState, voucherOfferList: OffersList[]) {
    state.voucherOffers.items = voucherOfferList
  },
  updatePartnerOffersIsLoading(state: MyCouponInfoState, loading: boolean) {
    state.partnerOffers.isLoading = loading
  },
  updatePartnerOffersList(state: MyCouponInfoState, partnerOfferList: PartnerOffersList[]) {
    state.partnerOffers.items = partnerOfferList
  }
}

export const store: Module<MyCouponInfoState, RootState> = {
  namespaced: true,
  state,
  mutations,
  actions,
}
